import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { IQueryContextDatasourceColumn } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { DatasourceColumnDataLoaderFactoryOptions } from '../../common/data-loaders/datasource-column';
import { DatasourceColumnHelper } from '../../common/data-loaders/datasource-column-helper';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';

export interface ChartDeviceDailyRevenueDataLoaderFactoryOptions
    extends
        ICreatedInstanceId,
        IGcpProjectId,
        IQueryCacheService,
        Pick<
            DatasourceColumnDataLoaderFactoryOptions,
            'filterContext' | 'ignoredContextColumnIds'
        > {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class ChartDeviceDailyRevenueDataLoaderFactory extends Factory<
    DataLoader,
    ChartDeviceDailyRevenueDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'ChartDeviceDailyRevenueDataLoaderFactory';

    getInstance(options: ChartDeviceDailyRevenueDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap = BiVizDashboardUsersActivity.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                dsColumns.present // context filter functionality
                    .filter((col: IQueryContextDatasourceColumn) =>
                        DatasourceColumnHelper.filterContextFn(col, options)
                    );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const {
                    dateTrunc,
                    splitSelectAsSeparateColumns,
                    splitGroupByTable,
                    where,
                    // is_in_context_app_version,
                    // unnestDatasourceColumn,
                    join,
                } = BiVizDashboardUsersActivity.compileSQLStatements(
                    dsColumns.present,
                    queryContext.settings,
                    options
                    // selectedDayNoSQL
                );

                // const selectFacts = is_in_context_app_version
                //     ? 'ds.date, ds.proper_id, app_version.*,'
                //     : 'ds.*,';

                const query = `
-- DataLoaderId: ${this.instanceId}

SELECT date
  , SUM(revenue_net_pln) AS revenue_net_pln
  , SUM(revenue_net_usd) AS revenue_net_usd
  , SUM(revenue_gross_pln) AS revenue_gross_pln
  , SUM(revenue_gross_usd) AS revenue_gross_usd
FROM \`${generateTableFullPath(
                    options.gcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                )}\` ds
${join}
${where}
GROUP BY date
ORDER BY date ASC
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);
                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'date',
                    'day'
                );

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
